import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import { useAuthStore } from '@/stores/authStore';
import { useNotify } from '@/hooks/useNotify';
import { useWorkOrderSignature } from '@/hooks/useWorkOrderSignature';
import type { WorkOrderApprovalState, WorkOrderSignatureStatus } from '@/types';
import {
  OPEN_APPROVAL_CARD_EVENT,
  WORK_ORDER_APPROVAL_CARD_ELEMENT_ID,
} from './signatureRequiredError';
import { lapseReasonLabel, ownershipDocumentLabel, signatureMethodLabel } from './signatureLabels';
import { TabletSignatureDialog } from './TabletSignatureDialog';
import { PhoneAttestationDialog } from './PhoneAttestationDialog';
import { OverrideSignatureDialog } from './OverrideSignatureDialog';
import { PaperSignatureDialog } from './PaperSignatureDialog';
import { OwnershipVerificationDialog } from './OwnershipVerificationDialog';

const OVERRIDE_PERMISSION = 'work_orders.signature_override';

const STATE_COLOR: Record<WorkOrderApprovalState, 'default' | 'warning' | 'success' | 'error'> = {
  not_required: 'default',
  pending: 'warning',
  valid: 'success',
  lapsed: 'error',
};

export interface WorkOrderApprovalCardProps {
  workOrderId: string;
  registryOwnerName?: string | null;
}

function stateLabel(
  t: TFunction,
  status: WorkOrderSignatureStatus,
): string {
  if (status.state === 'not_required') return t('signature.stateNotRequired');
  if (status.state === 'pending') return t('signature.statePending');
  if (status.state === 'valid' && status.latestApproval) {
    const method = signatureMethodLabel(t, status.latestApproval.method);
    const time = new Date(status.latestApproval.createdAt).toLocaleString();
    return t('signature.validSummary', {
      method,
      signer: status.latestApproval.signerName,
      time,
    });
  }
  if (status.state === 'lapsed') {
    const reason = status.lapse?.reasons[0]
      ? lapseReasonLabel(t, status.lapse.reasons[0])
      : t('signature.stateLapsed');
    return t('signature.lapsedSummary', { reason });
  }
  return t('signature.statePending');
}

export function WorkOrderApprovalCard({
  workOrderId,
  registryOwnerName = null,
}: WorkOrderApprovalCardProps) {
  const { t } = useTranslation('workorders');
  const notify = useNotify();
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const canOverride = hasPermission(OVERRIDE_PERMISSION);
  const signature = useWorkOrderSignature(workOrderId);
  const [tabletOpen, setTabletOpen] = useState(false);
  const [paperOpen, setPaperOpen] = useState(false);
  const [phoneOpen, setPhoneOpen] = useState(false);
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [ownershipOpen, setOwnershipOpen] = useState(false);
  const [highlight, setHighlight] = useState(false);

  useEffect(() => {
    const onOpen = () => setHighlight(true);
    window.addEventListener(OPEN_APPROVAL_CARD_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_APPROVAL_CARD_EVENT, onOpen);
  }, []);

  const status = signature.status;
  const needsAction = status?.state === 'pending' || status?.state === 'lapsed';
  const securityJob = status?.isSecurityJob === true;
  const showCapture = needsAction;
  const showPhone = showCapture && !securityJob;
  const showOverride = showCapture && !securityJob && canOverride;
  const showOwnership = securityJob === true;

  const chipLabel = useMemo(
    () => (status ? stateLabel(t, status) : t('signature.statePending')),
    [status, t],
  );

  return (
    <Box id={WORK_ORDER_APPROVAL_CARD_ELEMENT_ID} data-testid="work-order-approval-card">
      {highlight && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          onClose={() => setHighlight(false)}
          data-testid="signature-required-notice"
        >
          {t('signature.requiredError')}
        </Alert>
      )}

      {signature.isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      {signature.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t('signature.loadFailed')}
        </Alert>
      )}

      {status && (
        <Stack spacing={2}>
          <Chip
            label={chipLabel}
            color={STATE_COLOR[status.state]}
            data-testid={`signature-state-${status.state}`}
          />

          {showCapture && (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexWrap="wrap">
              <Button
                variant="contained"
                onClick={() => setTabletOpen(true)}
                data-testid="signature-action-tablet"
              >
                {t('signature.signTablet')}
              </Button>
              <Button
                variant="outlined"
                onClick={() => setPaperOpen(true)}
                data-testid="signature-action-paper"
              >
                {t('signature.attachPaper')}
              </Button>
              {showPhone && (
                <Button
                  variant="outlined"
                  onClick={() => setPhoneOpen(true)}
                  data-testid="signature-action-phone"
                >
                  {t('signature.recordPhone')}
                </Button>
              )}
              {showOverride && (
                <Button
                  variant="outlined"
                  color="warning"
                  onClick={() => setOverrideOpen(true)}
                  data-testid="signature-action-override"
                >
                  {t('signature.override')}
                </Button>
              )}
            </Stack>
          )}

          {showOwnership && (
            <Box>
              <Button
                variant="outlined"
                onClick={() => setOwnershipOpen(true)}
                data-testid="signature-action-ownership"
              >
                {t('signature.recordOwnership')}
              </Button>
              {status.latestOwnershipVerification && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {ownershipDocumentLabel(t, status.latestOwnershipVerification.documentType)}
                </Typography>
              )}
            </Box>
          )}
        </Stack>
      )}

      {status && (
        <TabletSignatureDialog
          open={tabletOpen}
          status={status}
          submitting={signature.tabletPending}
          onClose={() => setTabletOpen(false)}
          onSubmit={async (input) => {
            await signature.signTablet(input);
            setTabletOpen(false);
            notify.success(t('signature.savedTablet'), t('signature.savedTablet'));
          }}
        />
      )}

      <PaperSignatureDialog
        open={paperOpen}
        workOrderId={workOrderId}
        submitting={signature.paperPending}
        onClose={() => setPaperOpen(false)}
        onSubmit={async (attachmentId) => {
          await signature.attachPaper({ attachmentId });
          setPaperOpen(false);
          notify.success(t('signature.savedPaper'), t('signature.savedPaper'));
        }}
      />

      <PhoneAttestationDialog
        open={phoneOpen}
        submitting={signature.phonePending}
        onClose={() => setPhoneOpen(false)}
        onSubmit={async (input) => {
          await signature.recordPhone(input);
          setPhoneOpen(false);
          notify.success(t('signature.savedPhone'), t('signature.savedPhone'));
        }}
      />

      <OverrideSignatureDialog
        open={overrideOpen}
        submitting={signature.overridePending}
        onClose={() => setOverrideOpen(false)}
        onSubmit={async (reason) => {
          await signature.overrideSignature({ action: 'waive', reason });
          setOverrideOpen(false);
          notify.success(t('signature.savedOverride'), t('signature.savedOverride'));
        }}
      />

      <OwnershipVerificationDialog
        open={ownershipOpen}
        submitting={signature.ownershipPending}
        registryOwnerName={registryOwnerName}
        onClose={() => setOwnershipOpen(false)}
        onSubmit={async (input) => {
          await signature.recordOwnership(input);
          setOwnershipOpen(false);
          notify.success(t('signature.savedOwnership'), t('signature.savedOwnership'));
        }}
      />
    </Box>
  );
}

export function SignatureRequiredNotice({ onOpenCard }: { onOpenCard: () => void }) {
  const { t } = useTranslation('workorders');
  return (
    <Alert
      severity="warning"
      action={
        <Button color="inherit" size="small" onClick={onOpenCard}>
          {t('signature.openCard')}
        </Button>
      }
      data-testid="signature-required-banner"
    >
      {t('signature.requiredError')}
    </Alert>
  );
}
